import { useDispatch, useSelector, TypedUseSelectorHook } from "react-redux";
import type { AppDispatch } from "./configureStore";
import type { RootState } from "../reducers";

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Note OWNER_ROLE is the string "manage" — see config/constants.
const MANAGE_ROLES = ["manage", "owner"];

// Mirrors vid's AccountPolicy#manage? and owl's authorize_manage!: whether the
// signed-in user can administer the account they are currently viewing.
export const useCanManageAccount = (): boolean => {
  const currentAccount = useAppSelector((state) => state.auth.currentAccount);
  const accounts = useAppSelector((state) => state.auth.accounts);

  const account = accounts?.find((item: any) => item.id === currentAccount);
  return !!account?.roles?.some((role: string) => MANAGE_ROLES.includes(role));
};

// Mirrors owl's authenticate_owner! (owner only, not manage). Platform admins
// get every role from the accounts payload, so they pass too.
export const useIsAccountOwner = (): boolean => {
  const currentAccount = useAppSelector((state) => state.auth.currentAccount);
  const accounts = useAppSelector((state) => state.auth.accounts);

  const account = accounts?.find((item: any) => item.id === currentAccount);
  return !!account?.roles?.includes("owner");
};

// Mirrors owl's Account#has_feature?: whether the account being viewed has an
// account feature switched on (set per account on the platform admin page).
export const useAccountHasFeature = (feature: string): boolean => {
  const currentAccount = useAppSelector((state) => state.auth.currentAccount);
  const accounts = useAppSelector((state) => state.auth.accounts);

  const account = accounts?.find((item: any) => item.id === currentAccount);
  return !!account?.features?.includes(feature);
};

// Mirrors owl's require_admin!: platform admins (Riivet staff), whichever
// account they are viewing.
export const useIsPlatformAdmin = (): boolean => {
  const user = useAppSelector((state) => state.auth.currentUser);
  return !!user?.data?.roles?.includes("admin");
};
